<script setup lang="ts">
/* eslint-disable max-lines */
import { computed, nextTick, onBeforeMount, onMounted, reactive, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { retry, sleep } from 'radash';
import ProjectService from '@/core/shared/project/project.service';
import { EmployerService } from '@/core/shared/employer/employer.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import JobHeaders from '@/components/Jobs/JobHeaders/JobHeaders.vue';
import JobDetailsForm from '@/components/Jobs/CopilotActivation/JobDetailsForm.vue';
import JobDetailsFormSkeleton from '@/components/Jobs/CopilotActivation/JobDetailsFormSkeleton.vue';
import JobSkillsForm from '@/components/Jobs/CopilotActivation/JobSkillsForm.vue';
import CandidateScreeningForm from '@/components/Jobs/CopilotActivation/CandidateScreeningForm.vue';
import ExitCopilotActivationModal from '@/components/Jobs/CopilotActivation/ExitCopilotActivationModal.vue';
import VersionHistoryList from '@/components/Jobs/CopilotActivation/VersionHistoryList.vue';
import CopilotActivationButtons from '@/components/Jobs/CopilotActivation/CopilotActivationButtons.vue';
import SourcingStrategyForm from '@/components/Jobs/CopilotActivation/SourcingStrategyForm.vue';
import CopilotConfigurationTemplateForm from '@/components/Jobs/CopilotActivation/CopilotConfigurationTemplateForm.vue';
import UploadCandidatesForm from '@/components/Jobs/CopilotActivation/UploadCandidatesForm.vue';
import Divider from '@/components/Shared/Divider/Divider.vue';

import {
  FeatureName,
  JobStatus,
  PolicyName,
  type Project,
  ScreeningAction,
  ScreeningType,
} from '@factoryfixinc/ats-interfaces';
import type { VForm } from 'vuetify/components';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import { InvalidPostalCodeError } from '@/core/shared/errors/invalid-postal-code.error';
import {
  JobValidationError,
  formatJobValidationMessage,
} from '@/core/shared/errors/job-validation.error';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { determineJobUpdateSnackbar } from '@/core/jobs/copilot-activation/utils/job-update-snackbar.util';
import {
  ResumeUploadSource,
  TrackingActionName,
  TrackingCopilotSource,
} from '@/core/shared/tracking/tracking-actions';
import VersionsService from '@/core/jobs/versions/versions.service';
import { JobVersionViewStatus } from '@/core/jobs/versions/types/job-version-history.type';
import type { JobStatusHistory } from '@factoryfixinc/ats-interfaces/dist/types/job-status-history.model';
import { SearchService } from '@/core/sourcing/search/search.service';
import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import { MINIMUM_REQUIRED_SCREENER_QUESTIONS } from '@/core/jobs/copilot-activation/types/local-screener-question.type';
import CopilotConfigurationTemplateService from '@/core/copilot-configuration-template/copilot-configuration-template.service';
import InterviewSchedulingService from '@/core/shared/interview-scheduling/interview-scheduling.service';
import { EmployerFeatureService } from '@/core/shared/employer-feature/employer-feature.service';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import { OutreachService } from '@/core/sourcing/outreach/outreach.service';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';
import { projectLocationFor } from '@/core/jobs/copilot-activation/location-context.utils';

const copilotActivationService = new CopilotActivationService();
const projectService = new ProjectService();
const employerFeatureService = new EmployerFeatureService();
const employerFeatureGatingService = new EmployerFeatureGatingService();
const employerService = new EmployerService();
const subscriptionService = new SubscriptionService();
const jobVersionsService = new VersionsService();
const searchService = new SearchService();
const copilotConfigurationTemplateService = new CopilotConfigurationTemplateService();
const interviewSchedulingService = new InterviewSchedulingService();
const outreachService = new OutreachService();
const uploadCandidatesFormRef = ref<InstanceType<typeof UploadCandidatesForm> | null>(null);

enum FORM_SECTIONS {
  JOB_DETAILS = 'job-details',
  COPILOT_CONFIGURATION_TEMPLATE = 'copilot-configuration-template',
  MUST_HAVE_SKILLS = 'must-have-skills',
  SOURCING_STRATEGY = 'sourcing-strategy',
  SCREENING_STYLE = 'screening-style',
  INTERVIEW_SCHEDULING = 'interview-scheduling',
  UPLOAD_CANDIDATES = 'upload-candidates',
}

const display = useDisplay();
const route = useRoute();
const router = useRouter();

const isHeadersDrawerOpen = ref(true);
const isValidForm = ref(false);
const copilotForm = ref<VForm | null>(null);
const isExitModalOpen = ref(false);
const isExitInProgress = ref(false);
const isAbandoningCopilotFlow = ref(false);
const isLoadingCopilot = ref(false);
const isLoading = ref(false);
const project = ref<Project>();
const canEditForm = ref(false);
const originalJobTitle = ref('');
const screeningType = ref<ScreeningType>();
const jobStatusHistory = ref<JobStatusHistory[]>([]);
const clonedLocalJobDataInformation = ref('');
const shouldShowModal = ref<boolean>(false);
const showSkeletonLoader = ref(false);
// Only used when creating a new job
const isBlurringMode = ref(false);
const visitedSections = ref<FORM_SECTIONS[]>([FORM_SECTIONS.JOB_DETAILS]);
const showCompletionMessage = ref<boolean>(false);
const hasReceivedAnimationComplete = ref<boolean>(false);

const loadedRequirements = reactive({
  jobInformation: false,
  jobDetailsForm: true,
  jobSkillsForm: true,
  jobDescriptionForm: false,
});

// Computed properties
const isDesktop = computed(() => display.mdAndUp.value);
const isFormReady = computed(() => Object.values(loadedRequirements).every(Boolean));
const isDoingCandidateScreening = computed(() => employerFeatureService.hasUrlScreeningEnabled);
const isDoingZeroScreening = computed(() => employerFeatureService.hasZeroScreeningEnabled);
const selectedAction = computed<ScreeningAction | null>(
  () => copilotActivationService.screeningAction || ScreeningAction.SCREEN,
);
const hasInterviewSchedulingAccess = computed(() =>
  employerFeatureGatingService.hasAccess(FeatureName.INTERVIEW_SCHEDULING),
);

const isCreatingNewJob = computed(() => {
  return route.query.isCreatingJob === 'true';
});
const blurredSections = computed(() => {
  if (isBlurringMode.value) {
    return Object.values(FORM_SECTIONS).filter(
      (section) => !visitedSections.value.includes(section),
    );
  }
  return [];
});

const isCopilotConfigurationTemplateBlurred = computed(() => {
  return isSectionBlurred(FORM_SECTIONS.COPILOT_CONFIGURATION_TEMPLATE);
});

const isJobSkillsBlurred = computed(() => {
  return isSectionBlurred(FORM_SECTIONS.MUST_HAVE_SKILLS);
});

const isSourcingStrategyBlurred = computed(() => {
  return isSectionBlurred(FORM_SECTIONS.SOURCING_STRATEGY);
});

const isScreeningStyleBlurred = computed(() => {
  return isSectionBlurred(FORM_SECTIONS.SCREENING_STYLE);
});

const isDoingScreenerQuestions = computed(
  () =>
    !isDoingZeroScreening.value &&
    (!isDoingCandidateScreening.value ||
      (isDoingCandidateScreening.value && selectedAction.value === ScreeningAction.SCREEN)),
);

const projectId = computed(() => {
  const id = Number(route.params.id);
  return Number.isNaN(id) ? undefined : id;
});

const shouldValidate = computed(() => !!route.query.validate);

const hasAlreadyCopilotActivated = computed(() => {
  return copilotActivationService.jobStatus === JobStatus.LIVE;
});

const hasUsedAllJobSlots = computed(() => {
  return subscriptionService.usedJobSlots >= subscriptionService.jobSlots;
});

const isShowingAdditionalChargeNotice = computed(() => {
  return hasUsedAllJobSlots.value && !hasAlreadyCopilotActivated.value;
});

const isDisabled = computed(() => {
  return isLoading.value || jobVersionsService.isLoadingJobVersion || !canEditForm.value;
});

const isLoadingOneJobVersion = computed(() => jobVersionsService.isLoadingJobVersion);

const selectedVersionId = computed(() => jobVersionsService.currentJobVersionId);

const selectedJobVersionDate = computed(() => {
  if (jobVersionsService.currentJobVersion) {
    return VersionsService.formatJobVersionDate(
      new Date(jobVersionsService.currentJobVersion.createTs),
    );
  }
  return '';
});

const isCopilotActivated = computed(() => {
  return projectService.currentProject?.copilot;
});

const calculateLocation = computed(() => {
  const { city, state, locationContext } = copilotActivationService;
  return projectLocationFor(locationContext, city, state);
});

const shouldUpdateSavedSearch = computed(() => {
  const displayTitle = (copilotActivationService.displayTitle ?? '').trim();
  return originalJobTitle.value === '' && displayTitle !== '';
});

const copilotTrackingStatus = computed(() => {
  return hasAlreadyCopilotActivated.value ? 'activated' : 'deactivated';
});

const isAtsSynced = computed(() => (!project.value ? false : project.value.remoteJobId !== null));

// Check if this is a new project by verifying if all conditions are true:
// 1. There are no job versions (new job)
// 2. The project's update timestamp is very close to its creation timestamp (indicating no actual updates)
// 3. The project has no title or has the default "No Title"
const isNewProject = computed(() => {
  if (!project.value) return false;

  const updateTime = new Date(project.value.updateTs || 0).getTime();
  const createTime = new Date(project.value.createTs || 0).getTime();
  const timeDifferenceInSeconds = Math.abs(updateTime - createTime) / 1000;
  const isRecentlyCreated = timeDifferenceInSeconds <= 5;

  const noJobVersions = jobVersionsService.jobVersions.length === 0;

  // Edge case: some times the side effect creates the first version when the user lands on the form
  const isOneJobVersion = jobVersionsService.jobVersions.length === 1;
  const isRecentlyVersionCreated =
    isOneJobVersion &&
    Math.abs(new Date(jobVersionsService.jobVersions[0].createTs).getTime() - createTime) / 1000 <=
      5;

  const projectTitle = (project.value.title || '').trim().toLowerCase();
  const hasDefaultOrNoTitle = !projectTitle || projectTitle === 'no title';

  return (noJobVersions || isRecentlyVersionCreated) && isRecentlyCreated && hasDefaultOrNoTitle;
});

const wasCopilotActivated = computed(() => {
  return jobStatusHistory.value.some((history) => history.status === JobStatus.LIVE);
});

const isJobVersionEditMode = computed(() => {
  return jobVersionsService.currentJobVersionViewStatus === JobVersionViewStatus.EDIT_MODE;
});

const isJobVersionViewMode = computed(() => {
  return jobVersionsService.currentJobVersionViewStatus === JobVersionViewStatus.VIEW_MODE;
});

const shouldShowCompletionMessage = computed(() => {
  return (
    showCompletionMessage.value && hasReceivedAnimationComplete.value && isCreatingNewJob.value
  );
});

const copilotConfigurationTemplate = computed(() => {
  const templateId = copilotActivationService.copilotConfigurationTemplateId;

  return copilotConfigurationTemplateService.templates.find(
    (template) => template.id === templateId,
  );
});

const selectedCopilotConfigurationTemplateDisplayName = computed(() => {
  return copilotConfigurationTemplate.value?.displayName || 'The AI recruiter';
});

const copilotOutreachPolicy = computed(() =>
  employerService.getPolicyByName(PolicyName.COPILOT_OUTREACH),
);

const scheduledUpdatesPolicy = computed(() =>
  employerService.getPolicyByName(PolicyName.SCHEDULED_UPDATES),
);

const interviewSchedulingPolicy = computed(() =>
  employerService.getPolicyByName(PolicyName.INTERVIEW_SCHEDULING),
);

const dynamicScreeningPolicy = computed(() =>
  employerService.getPolicyByName(PolicyName.DYNAMIC_SCREENING),
);

const staticScreeningPolicy = computed(() =>
  employerService.getPolicyByName(PolicyName.STATIC_SCREENING),
);

const candidateAiDisclosurePolicy = computed(() =>
  employerService.getPolicyByName(PolicyName.AI_DISCLOSURE),
);

async function handleCopilotAnimationComplete() {
  hasReceivedAnimationComplete.value = true;

  // Add all FORM_SECTIONS to visitedSections to unblur everything
  visitedSections.value = Object.values(FORM_SECTIONS);
  isBlurringMode.value = false;
  showCompletionMessage.value = true;

  await nextTick();

  // Scroll to bottom
  const formElement = document.getElementById('completion-message');
  if (formElement) {
    const rect = formElement.getBoundingClientRect();
    const offset = window.scrollY + rect.bottom - window.innerHeight + 100;
    window.scrollTo({ top: offset, behavior: 'smooth' });
  }
}

async function loadProjectInformation() {
  try {
    if (!projectId.value) {
      await redirectToConversations();
      return;
    }
    project.value = await projectService.getProjectById(projectId.value);
    await loadJobInformation(project.value);

    loadedRequirements.jobInformation = true;
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to load job. Please try again later.');
    redirectToConversations();
  }
}

async function loadJobInformation(project: Project) {
  try {
    const job = await copilotActivationService.setupStoreByProject(project);
    if (job) {
      jobStatusHistory.value = job.jobStatusHistories ?? [];
      originalJobTitle.value = copilotActivationService.displayTitle ?? '';
      screeningType.value = job.screeningType;
    }
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to load job. Please try again later.');
  }
}

async function redirectToConversations() {
  return router.push('/conversations');
}

function exitCopilotFlow() {
  if (window.history.length > 1) {
    router.back();
    return;
  }
  router.push(`/sourcing/job/${projectId.value}`);
}

function handleExitModalCancel() {
  isExitModalOpen.value = false;
  isExitInProgress.value = false;
  isAbandoningCopilotFlow.value = false;
}

function handleExitModalExit() {
  if (isExitInProgress.value) {
    return;
  }

  isExitInProgress.value = true;
  isAbandoningCopilotFlow.value = true;
  exitCopilotFlow();
}

async function handleInterviewSchedulingUpdate() {
  if (!hasInterviewSchedulingAccess.value) return;
  if (!projectId.value) return;
  try {
    const defaultSettings = copilotActivationService.getDefaultInterviewSettings();
    await interviewSchedulingService.upsertInterviewSettings(
      projectId.value,
      copilotActivationService.interviewSettings ?? defaultSettings,
    );
  } catch (error) {
    ErrorService.captureException(error);
  }
}

async function handleDeferredResumeUpload() {
  const jobId = copilotActivationService.selectedJobId;
  if (!uploadCandidatesFormRef.value?.hasStagedFiles || !jobId) return;

  try {
    const staged = await uploadCandidatesFormRef.value.getStagedFilesForUpload();
    if (!staged) return;

    await outreachService.uploadCandidateResumes(jobId, staged.files, staged.engagementMode, {
      source: ResumeUploadSource.ACTIVATION_WIZARD,
    });
  } catch (error) {
    ErrorService.captureException(error);
  }
}

async function updateJob(payload: { activate: boolean; byPassModal?: boolean }) {
  payload.byPassModal = payload.byPassModal ?? false;
  const { activate, byPassModal } = payload;
  try {
    const shouldValidateForm = activate || !!isCopilotActivated.value;
    shouldShowModal.value = byPassModal;
    isLoadingCopilot.value = activate; // To show the loading spinner for the Copilot button
    isLoading.value = !activate; //  or the Save & Exit button individually
    copilotActivationService.hasTriedToUpdateJob = shouldValidateForm;

    // Validate the form if we are activating Copilot or its already activated
    if (copilotForm.value && shouldValidateForm) {
      await copilotForm.value.validate();
    }

    // Double check screener questions as those handle validation independently.
    const isCheckingScreenerQuestions = isDoingScreenerQuestions.value;
    let areScreenerQuestionsValid = true;
    if (isCheckingScreenerQuestions && shouldValidateForm) {
      if (copilotActivationService.screeningType == ScreeningType.STATIC) {
        const validQuestions = copilotActivationService.screenerQuestions.filter(
          (question) => question.text.trim().length > 0,
        );

        areScreenerQuestionsValid = validQuestions.length >= MINIMUM_REQUIRED_SCREENER_QUESTIONS;
      }
    }

    // Also double check the job description validity.
    const validJobDescription = `${copilotActivationService.rawDescription}`.trim().length > 0;

    // Double check the interview settings validity.
    const settings = copilotActivationService.interviewSettings;

    /** Validate only if we're in SCREEN action and they are enabled.
     * Choosing URL screening will set enabled to false
     */
    const shouldValidateInterviewSettings =
      selectedAction.value === ScreeningAction.SCREEN && settings?.enabled;

    const areInterviewSettingsValid = shouldValidateInterviewSettings
      ? interviewSchedulingService.validateInterviewSettings(
          copilotActivationService.interviewSettings ?? null,
        )
      : true;

    // Only validate the form if we are activating the job or if the Job is already Copilot activated
    if (
      shouldValidateForm &&
      (!isValidForm.value ||
        !areScreenerQuestionsValid ||
        !validJobDescription ||
        !areInterviewSettingsValid ||
        !projectId.value)
    ) {
      // If the form is invalid, wait for a brief moment, find the first invalid input field, and scroll to it smoothly.
      await sleep(300);
      const firstInvalidInput = document.querySelector('.v-field--error');
      if (firstInvalidInput) {
        const rect = firstInvalidInput.getBoundingClientRect();
        const offset = window.scrollY + rect.top - 32;
        window.scrollTo({ top: offset, behavior: 'smooth' });
      }
      return;
    }

    const jobUpdateOutcome = await copilotActivationService.updateJob(
      copilotActivationService.scheduledUpdates,
    );

    if (projectId.value) {
      const projectCopilotConfigurationSnapshot = {
        ...copilotActivationService.projectCopilotConfiguration,
      };
      projectCopilotConfigurationSnapshot.includeAiDisclosure =
        ProjectCopilotConfigurationUtils.normalizeIncludeAiDisclosure(
          candidateAiDisclosurePolicy.value,
          projectCopilotConfigurationSnapshot.includeAiDisclosure,
        );

      // Update the project title and location since we eliminated the Modal
      await projectService.updateProject(projectId.value, {
        title: copilotActivationService.displayTitle ?? '',
        location: calculateLocation.value,
        generateQueryForSavedSearch: shouldUpdateSavedSearch.value,
        scheduledUpdates: copilotActivationService.scheduledUpdates,
        projectCopilotConfiguration: {
          version: 'v1',
          copilotConfigurationTemplateId: copilotActivationService.copilotConfigurationTemplateId,
          snapshot: JSON.stringify(projectCopilotConfigurationSnapshot),
        },
      });

      await handleInterviewSchedulingUpdate();

      if (activate) {
        await projectService.updateProjectCopilotStatus(projectId.value, true);

        // Only track the abandoned event if copilot was not active
        if (!hasAlreadyCopilotActivated.value) {
          TrackingService.trackAction(TrackingActionName.COPILOT_ENABLE_COMPLETED, {
            source: TrackingCopilotSource.COPILOT_ACTIVATION_PAGE,
            project_id: projectId.value,
            job_id: copilotActivationService.selectedJobId,
          });
          // Update the local value for the UI since the DB change is done as a side effect
          copilotActivationService.jobStatus = JobStatus.LIVE;
        }

        // Deferred upload: upload staged resumes after successful activation
        await handleDeferredResumeUpload();
      } else {
        TrackingService.trackAction(TrackingActionName.COPILOT_ENABLE_CHANGES_SAVED, {
          source: TrackingCopilotSource.COPILOT_ACTIVATION_PAGE,
          copilot_active: copilotTrackingStatus.value,
          project_id: projectId.value,
          job_id: copilotActivationService.selectedJobId,
        });
      }
      // We need to reload the project to get the updated values
      // or else if we go back, the Saved Search will not be updated
      await projectService.getProjectById(projectId.value);
      searchService.setSelectedFiltersFromProject(projectService.currentProject);
    }

    const jobUpdateSnackbar = determineJobUpdateSnackbar(jobUpdateOutcome);
    SnackbarService[jobUpdateSnackbar.severity](jobUpdateSnackbar.message);
    if (jobUpdateSnackbar.shouldExit) {
      exitCopilotFlow();
    }
  } catch (error) {
    if (error instanceof JobValidationError) {
      SnackbarService.caution(formatJobValidationMessage(error.fields));
    } else if (error instanceof InvalidPostalCodeError) {
      SnackbarService.critical('Invalid ZIP code. Please edit the job and try again.');
    } else {
      ErrorService.captureException(error);
      SnackbarService.critical('Failed to update job. Please try again later.');
    }
  } finally {
    isLoading.value = false;
    isLoadingCopilot.value = false;
  }
}

onMounted(async () => {
  loadProjectInformation();
  canEditForm.value = true;
  window.scrollTo({ top: 0, behavior: 'auto' }); // Scroll top

  if (isCreatingNewJob.value) {
    isBlurringMode.value = true;
  }

  // When on the create new job, fetch the copilot configuration templates
  // so that if the non default copilot is selected, the template data
  // is available for the completion message shown on the bottom of the form
  // after it is selected
  await copilotConfigurationTemplateService.fetchTemplates();
});

onBeforeMount(() => {
  jobVersionsService.resetJobVersions();
});

watch(projectId, (newValue) => {
  if (!newValue) return redirectToConversations();
});

watch(isFormReady, (newValue) => {
  if (newValue) {
    window.scrollTo({ top: 0, behavior: 'auto' }); // Scroll top

    // We need to clone the localJobData to compare it later to the Form data changes made by the user
    clonedLocalJobDataInformation.value = JSON.stringify(
      copilotActivationService.localJobDataInformation,
    );

    // If we come from the Copilot Activation button from ProjectCard,
    // we need to validate the form to show the user the errors
    if (shouldValidate.value) {
      setTimeout(() => updateJob({ activate: true, byPassModal: false }), 750);
    }
  }
});

watch(isCreatingNewJob, (newValue) => {
  isBlurringMode.value = newValue;
});

/**
 * On an attempt to leave the page, show the exit modal, else allow leaving the page
 * if the form is valid or if the exit modal is already visible. When leaving the page,
 * refresh the projects list.
 */
onBeforeRouteLeave(async (to, from, next) => {
  const page = document.getElementsByTagName('body')[0];
  page.classList.remove('noscroll');

  if (shouldShowModal.value || isValidForm.value || isAbandoningCopilotFlow.value) {
    if (isAbandoningCopilotFlow.value) {
      try {
        const currentProjectId = projectId.value;
        if (isNewProject.value && currentProjectId !== undefined) {
          await projectService.archiveProject(currentProjectId);
          await retry({ times: 4, delay: 2000 }, () =>
            projectService.deleteProject(currentProjectId),
          );
        }
      } catch (error) {
        ErrorService.captureException(error);
      }
    }

    await projectService.searchProjects({ skipTracking: true });

    if (isAbandoningCopilotFlow.value) {
      TrackingService.trackAction(TrackingActionName.COPILOT_ENABLE_ABANDONED, {
        project_id: projectId.value,
        job_id: copilotActivationService.selectedJobId,
      });
    }

    //If we exit the FORM, we need to reset the Job Versions for the project
    jobVersionsService.currentJobVersion = null;
    jobVersionsService.currentJobVersionId = null;
    copilotActivationService.scheduledUpdates = false;
    next();
    return;
  }

  isExitModalOpen.value = true;

  next(false);
});

watch(selectedVersionId, async (newValue, oldValue) => {
  if (newValue === oldValue) {
    return Promise.resolve();
  }

  showSkeletonLoader.value = true;

  if (jobVersionsService.jobVersionWithRelations) {
    // Project is empty, redirect to conversations.
    // The project should be already set by the time we get here.
    if (!project.value) {
      await redirectToConversations();
      showSkeletonLoader.value = false;
      return;
    }

    await copilotActivationService.setupStoreByProject(project.value, true);

    // If the selected ID is the same as the first version in the list then we are in normal mode.
    if (jobVersionsService.jobVersions[0].id === newValue) {
      jobVersionsService.currentJobVersionViewStatus = JobVersionViewStatus.NORMAL_MODE;
      canEditForm.value = true;
    } else {
      jobVersionsService.currentJobVersionViewStatus = JobVersionViewStatus.VIEW_MODE;
      canEditForm.value = false;
    }
  }
  showSkeletonLoader.value = false;
});

// To disable scrolling when loading a job version
watch(isLoadingOneJobVersion, (newValue) => {
  const page = document.getElementsByTagName('body')[0];
  if (newValue) {
    page.classList.add('noscroll');
  } else {
    page.classList.remove('noscroll');
  }
  showSkeletonLoader.value = newValue;
});

const enableJobVersionEditing = () => {
  jobVersionsService.currentJobVersionViewStatus = JobVersionViewStatus.EDIT_MODE;
  canEditForm.value = true;
};

const disableJobVersionEditing = () => {
  jobVersionsService.currentJobVersionViewStatus = JobVersionViewStatus.VIEW_MODE;
  canEditForm.value = false;
};

async function disableCopilot() {
  // Since we are disabling Copilot, will navigate to the previous page
  // so we dont need to show the modal
  shouldShowModal.value = false;

  if (!projectId.value) return;
  try {
    isLoadingCopilot.value = true;
    await projectService.updateProjectCopilotStatus(projectId.value, false);
    copilotActivationService.jobStatus = JobStatus.DRAFT;

    TrackingService.trackAction(TrackingActionName.COPILOT_DISABLED, {
      source: TrackingCopilotSource.COPILOT_ACTIVATION_PAGE,
      project_id: projectId.value,
      job_id: copilotActivationService.selectedJobId,
    });

    SnackbarService.success('Job deactivated successfully');
    exitCopilotFlow();
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to deactivate job. Please try again later.');
  } finally {
    isLoadingCopilot.value = false;
  }
}

function isSectionBlurred(section: FORM_SECTIONS) {
  return blurredSections.value.includes(section);
}

function handleUnblurSection(section: FORM_SECTIONS) {
  if (isSectionBlurred(section)) {
    visitedSections.value.push(section);
  }
}
</script>

<template>
  <div class="h-full w-full pl-8">
    <v-navigation-drawer
      width="336"
      v-model:model-value="isHeadersDrawerOpen"
      color="shade-860"
      class="px-4 pt-8"
      :permanent="isDesktop"
    >
      <JobHeaders
        v-if="isFormReady"
        :show-upload-candidates="!hasAlreadyCopilotActivated"
        @click:back="isExitModalOpen = true"
      />
    </v-navigation-drawer>
    <div class="form-container">
      <!-- Main Content -->
      <div class="flex">
        <v-form
          v-model="isValidForm"
          v-show="isFormReady"
          class="copilot-activation-form mx-auto max-w-[696px] py-7"
          ref="copilotForm"
          @submit.prevent
        >
          <template v-if="showSkeletonLoader">
            <JobDetailsFormSkeleton />
          </template>
          <template v-else>
            <div v-if="isJobVersionEditMode" class="banner restored">
              <strong>Restored version ({{ selectedJobVersionDate }})</strong>. Save this version to
              overwrite the active job posting.
            </div>
            <div v-if="isJobVersionViewMode" class="banner view-only">
              You are viewing an older version.
              <a
                href="#"
                class="font-sans text-sm font-semibold text-highlight-600 transition-colors hover:!text-highlight-800"
                @click.prevent="enableJobVersionEditing"
                >Restore</a
              >
              this version to edit it.
            </div>
            <span v-if="isJobVersionViewMode" class="float-right inline-block">
              <v-btn
                :ripple="false"
                class="modal-button-secondary"
                variant="flat"
                @click.prevent="enableJobVersionEditing"
                >Restore to edit</v-btn
              >
            </span>
            <JobDetailsForm
              :is-disabled="isDisabled"
              :is-continue-logic-enabled="isCreatingNewJob"
              @init:job-description-form="loadedRequirements.jobDescriptionForm = true"
              @click:continue="handleUnblurSection(FORM_SECTIONS.COPILOT_CONFIGURATION_TEMPLATE)"
            />
            <CopilotConfigurationTemplateForm
              :candidate-ai-disclosure-policy="candidateAiDisclosurePolicy"
              :is-disabled="isDisabled || isCopilotConfigurationTemplateBlurred"
              :is-blurred="isCopilotConfigurationTemplateBlurred"
              :is-continue-logic-enabled="isCreatingNewJob"
              @click:continue="handleUnblurSection(FORM_SECTIONS.MUST_HAVE_SKILLS)"
              @continue:with-animation="handleCopilotAnimationComplete"
            />
            <JobSkillsForm
              :is-disabled="isDisabled || isJobSkillsBlurred"
              :is-blurred="isJobSkillsBlurred"
              :is-continue-logic-enabled="isCreatingNewJob"
              @click:continue="handleUnblurSection(FORM_SECTIONS.SOURCING_STRATEGY)"
            />
            <SourcingStrategyForm
              :copilot-outreach-policy="copilotOutreachPolicy"
              :scheduled-updates-policy="scheduledUpdatesPolicy"
              :is-disabled="isDisabled || isSourcingStrategyBlurred"
              :is-blurred="isSourcingStrategyBlurred"
              :project-id="projectId"
              :copilot-status="copilotTrackingStatus"
              :is-continue-logic-enabled="isCreatingNewJob"
              @click:continue="handleUnblurSection(FORM_SECTIONS.SCREENING_STYLE)"
            />

            <template v-if="!isDoingZeroScreening">
              <CandidateScreeningForm
                :candidate-ai-disclosure-policy="candidateAiDisclosurePolicy"
                :interview-scheduling-policy="interviewSchedulingPolicy"
                :static-screening-policy="staticScreeningPolicy"
                :dynamic-screening-policy="dynamicScreeningPolicy"
                :has-url-screening-enabled="isDoingCandidateScreening"
                :is-disabled="isDisabled || isScreeningStyleBlurred"
                :is-blurred="isScreeningStyleBlurred"
                :is-continue-logic-enabled="isCreatingNewJob"
                @click:continue="handleUnblurSection(FORM_SECTIONS.INTERVIEW_SCHEDULING)"
              />
            </template>

            <template v-if="!hasAlreadyCopilotActivated">
              <Divider class="my-10" />
              <UploadCandidatesForm
                ref="uploadCandidatesFormRef"
                :job-id="copilotActivationService.selectedJobId"
                :active-outreach-enabled="copilotActivationService.activeOutreachEnabled"
              />
            </template>

            <div
              v-if="shouldShowCompletionMessage"
              id="completion-message"
              class="mb-4 mt-6 flex items-start gap-3 rounded-lg bg-[#BBF7D0] p-4"
            >
              <div class="mt-2 h-2 w-2 animate-pulse rounded-full bg-[#22C55E]"></div>
              <div>
                <div class="text-base font-medium text-[#14532D]">
                  {{ selectedCopilotConfigurationTemplateDisplayName }}
                  is configured and ready to begin sourcing candidates for this position
                </div>
                <div class="mt-1 text-sm text-[#15803D]">
                  All settings have been applied based on
                  {{ selectedCopilotConfigurationTemplateDisplayName }}'s profile. Review sections
                  above or activate now.
                </div>
              </div>
            </div>
          </template>
        </v-form>
        <div
          v-show="isFormReady"
          class="ml-8 w-[305px] min-w-[305px] border-l-[1px] border-tint-40 p-6"
        >
          <p class="mb-4 font-sans text-lg font-extrabold leading-7 text-black">Version History</p>
          <div v-if="isJobVersionEditMode" class="restored-list-item">
            Restored version ({{ selectedJobVersionDate }})
            <p>Unpublished</p>
          </div>
          <VersionHistoryList
            :key="projectId"
            :is-job-version-edit-mode="isJobVersionEditMode"
            :project-id="projectId"
            :is-copilot-activated="hasAlreadyCopilotActivated || wasCopilotActivated"
          />
        </div>
      </div>
      <template v-if="!isJobVersionViewMode">
        <CopilotActivationButtons
          v-show="isFormReady && !showSkeletonLoader"
          class="sticky-button-container"
          :isLoading="isLoading"
          :isLoadingCopilot="isLoadingCopilot"
          :isCopilotActivated="isCopilotActivated"
          :isJobVersionEditMode="isJobVersionEditMode"
          :isAtsSynced="isAtsSynced"
          :isShowingAdditionalChargeNotice="isShowingAdditionalChargeNotice"
          @update-job="updateJob"
          @disable-copilot="disableCopilot"
          @disable-job-version-editing="disableJobVersionEditing"
        />
      </template>
    </div>
    <div v-show="!isFormReady" class="flex h-full w-full items-center justify-center">
      <SpinnerLoader />
    </div>
    <ExitCopilotActivationModal
      :model-value="isExitModalOpen"
      :is-exiting="isExitInProgress"
      @cancel="handleExitModalCancel"
      @exit="handleExitModalExit"
    />
  </div>
</template>

<style lang="postcss" src="./copilot-activation.css" scoped></style>
